import { LayerItem } from "./layer-item"
import { CollapsibleSubgroup } from "./collapsible-subgroup"
import type { LayerGroup } from "@/types/explore"

const GROUP_COLORS: Record<string, string> = {
  primary: "#1E40AF",
  error: "#FB7D5B",
  success: "#46A097",
  warning: "#FCC43E",
  info: "#0288D1",
}

const GROUP_BG: Record<string, string> = {
  primary: "rgba(30, 64, 175, 0.04)",
  error: "rgba(251, 125, 91, 0.04)",
  success: "rgba(70, 160, 151, 0.04)",
  warning: "rgba(252, 196, 62, 0.04)",
  info: "rgba(2, 136, 209, 0.04)",
}

interface LayerGroupSectionProps {
  group: LayerGroup
  activeLayers: string[]
  toggleLayer: (id: string) => void
  quickLayers: string[]
  toggleQuickLayer: (id: string) => void
  onFilterChange: (layerId: string, filterId: string, value: unknown) => void
  forceSubgroupsOpen: boolean
  dynamicOptions?: Record<string, string[]>
}

export const LayerGroupSection = ({
  group,
  activeLayers,
  toggleLayer,
  quickLayers,
  toggleQuickLayer,
  onFilterChange,
  forceSubgroupsOpen,
  dynamicOptions,
}: LayerGroupSectionProps) => {
  const catColor = GROUP_COLORS[group.color] || "#9CA3AF"
  const catBg = GROUP_BG[group.color] || "transparent"

  return (
    <div
      className="layer-category rounded-lg overflow-hidden"
      style={{ "--cat-color": catColor } as React.CSSProperties}
    >
      {/* Group header */}
      <div className="flex items-center gap-2 px-4 py-1.5 rounded-lg" style={{ backgroundColor: catBg }}>
        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: catColor }} />
        <span className="text-xs font-bold uppercase tracking-widest" style={{ color: catColor }}>
          {group.label}
        </span>
      </div>

      {/* Group layers */}
      <div className="pl-2">
        {group.layers.map((layer) => (
          <LayerItem
            key={layer.id}
            layer={layer}
            active={activeLayers.includes(layer.id)}
            onToggle={() => toggleLayer(layer.id)}
            isQuick={quickLayers.indexOf(layer.id) !== -1 ? quickLayers.indexOf(layer.id) + 1 : undefined}
            onToggleQuick={toggleQuickLayer}
            onFilterChange={(fId, v) => onFilterChange(layer.id, fId, v)}
            dynamicOptions={dynamicOptions}
          />
        ))}
      </div>

      {/* Subgroups */}
      {group.subgroups?.map((sub) => (
        <div key={sub.label} className="pl-2">
          <CollapsibleSubgroup
            label={sub.label}
            layers={sub.layers}
            activeLayers={activeLayers}
            toggleLayer={toggleLayer}
            quickLayers={quickLayers}
            onToggleQuick={toggleQuickLayer}
            onFilterChange={onFilterChange}
            forceOpen={forceSubgroupsOpen}
          />
        </div>
      ))}
    </div>
  )
}
